import React, { useEffect, useState } from "react";
import {
  Container,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Label,
  Input,
  FormGroup,
  Badge,
} from "reactstrap";
import { AlternativePagination } from "../../components/Common/Table";
import "../../global.css";
import {
  GetVariants,
  AddVariant,
  UpdateVariant,
  DeleteVariant,
} from "../../requests";
import LoadingSpinner from "../../components/LoadingSpinner";
import { toast } from 'react-toastify';
import Breadcrumbs from "../../components/Common/Breadcrumb";

const Variants = () => {
  document.title = "Variant Types | Susipizza";

  const [modal, setModal] = useState(false);
  const [name, setName] = useState("");
  const [values, setValues] = useState([]);
  const [newValueInput, setNewValueInput] = useState("");
  const [variantId, setVariantId] = useState("");
  const [variantData, setVariantData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [buttonText, setButtonText] = useState("Save");
  const [confirmModal, setConfirmModal] = useState(false);
  const [targetId, setTargetId] = useState(null);

  const toggle = () => {
    if (modal) {
      setName("");
      setValues([]);
      setNewValueInput("");
      setEditingId(null);
      setButtonText("Save");
      setVariantId("");
    }
    setModal(!modal);
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);
    // Auto-slugify Variant ID for new variant types
    if (!editingId) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-") // replace non-alphanumeric with hyphen
        .replace(/^-+|-+$/g, ""); // trim leading/trailing hyphens
      setVariantId(slug);
    }
  };

  const handleAddValue = (e) => {
    if (e) e.preventDefault();
    const val = newValueInput.trim();
    if (!val) return;
    if (values.includes(val)) {
      toast.warning("Value already exists in the list.");
      return;
    }
    setValues([...values, val]);
    setNewValueInput("");
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddValue();
    }
  };

  const removeValue = (valToRemove) => {
    setValues(values.filter(v => v !== valToRemove));
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setName(row.name);
    setValues(row.values || []);
    setVariantId(row.variantId || row.id);
    setButtonText("Update");
    setModal(true);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Variant name is required");
      return;
    }
    if (!variantId.trim()) {
      toast.error("Variant ID is required");
      return;
    }
    if (values.length === 0) {
      toast.error("At least one variant value is required");
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const payload = {
        name: name,
        values: values,
        variantId: variantId,
      };

      if (editingId) {
        await UpdateVariant(token, editingId, payload);
        toast.success('Variant type updated successfully');
      } else {
        await AddVariant(token, payload);
        toast.success('Variant type added successfully');
      }
      toggle();
      getData();
    } catch (err) {
      console.error("Save variant type error:", err.response?.data || err);
      const validationErrs = err.response?.data?.validationErrors;
      if (validationErrs && Array.isArray(validationErrs)) {
        validationErrs.forEach(e => toast.error(`${e.field}: ${e.message}`));
      } else {
        const errorMsg = err.response?.data?.error?.message || err.response?.data?.error || 'Error occurred while saving variant type';
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
    setTargetId(id);
    setConfirmModal(true);
  };

  const confirmDelete = async () => {
    setConfirmModal(false);
    if (!targetId) return;
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      await DeleteVariant(token, targetId);
      toast.success("Variant type removed successfully");
      getData();
    } catch (err) {
      console.error(err);
      toast.error("Error occurred while removing variant type");
    } finally {
      setLoading(false);
      setTargetId(null);
    }
  };

  const getData = async () => {
    try {
      setLoading(true);
      const resp = await GetVariants();
      setVariantData(resp.data.data || resp.data);
    } catch (err) {
      console.error("Error getting variants data:", err);
      toast.error("Failed to load variant types");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const columns = [
    {
      name: <span className="font-weight-bold fs-13">SR No.</span>,
      selector: (row, index) => (index + 1).toString(),
      width: "80px",
    },
    {
      name: <span className="font-weight-bold fs-13">Variant ID</span>,
      selector: (row) => row.variantId || row.id,
      width: "180px",
    },
    {
      name: <span className="font-weight-bold fs-13">Name</span>,
      selector: (row) => row.name,
      width: "180px",
    },
    {
      name: <span className="font-weight-bold fs-13">Values</span>,
      cell: (row) => (
        <div className="d-flex flex-wrap gap-1 py-1">
          {(row.values || []).map((val, idx) => (
            <Badge key={idx} color="soft-primary" className="badge-soft-primary fs-11">
              {val}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      name: <span className="font-weight-bold fs-13">Action</span>,
      width: "110px",
      cell: (row) => (
        <div className="d-flex gap-2 align-items-center">
          <button
            className="btn btn-sm btn-icon btn-outline-primary"
            onClick={() => startEdit(row)}
            title="Edit Variant Type"
          >
            <i className="mdi mdi-pencil"></i>
          </button>
          <button
            className="btn btn-sm btn-icon btn-outline-danger"
            onClick={() => handleDeleteClick(row.variantId || row.id)}
            title="Remove Variant Type"
          >
            <i className="mdi mdi-trash-can-outline"></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <React.Fragment>
      <LoadingSpinner loading={loading}/>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="susipizza" breadcrumbItem="Variant Types" />

          <button type="button" className="btn btn-primary mb-3" onClick={toggle}>
            Add Variant Type
          </button>
          <AlternativePagination columns={columns} data={variantData} />
        </Container>
      </div>

      {/* Modal */}
      <Modal isOpen={modal} toggle={toggle} centered>
        <ModalHeader toggle={toggle}>{editingId ? "Edit Variant Type" : "Add Variant Type"}</ModalHeader>
        <ModalBody className="modal-color">
          <FormGroup>
            <Label for="variantName">Variant Type Name</Label>
            <Input
              id="variantName"
              name="name"
              placeholder="e.g. Size"
              value={name}
              onChange={handleNameChange}
            />
          </FormGroup>
          <FormGroup>
            <Label for="variantIdInput">Variant ID (System Code)</Label>
            <Input
              id="variantIdInput"
              name="variantId"
              placeholder="e.g. size"
              value={variantId}
              onChange={(e) => setVariantId(e.target.value.replace(/[^a-zA-Z0-9-]/g, ""))}
              disabled={!!editingId}
              required
            />
            <small className="text-muted">
              Must be unique. Alphanumeric and hyphens only. Used for referencing in system logic.
            </small>
          </FormGroup>

          <FormGroup>
            <Label>Values</Label>
            <div className="d-flex gap-2 mb-2">
              <Input
                type="text"
                placeholder="Type value (e.g. Small) and press enter"
                value={newValueInput}
                onChange={(e) => setNewValueInput(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <Button color="secondary" onClick={handleAddValue}>Add</Button>
            </div>
            
            <div className="border rounded p-2 bg-light d-flex flex-wrap gap-2" style={{ minHeight: "80px" }}>
              {values.length === 0 ? (
                <span className="text-muted align-self-center w-100 text-center fs-12">No values added yet.</span>
              ) : (
                values.map((val, idx) => (
                  <Badge
                    key={idx}
                    color="primary"
                    className="d-flex align-items-center gap-1 py-1.5 px-2 fs-12"
                    style={{ borderRadius: "4px" }}
                  >
                    {val}
                    <i
                      className="mdi mdi-close-circle cursor-pointer text-white-50"
                      onClick={() => removeValue(val)}
                      style={{ fontSize: "14px", lineHeight: "1" }}
                    ></i>
                  </Badge>
                ))
              )}
            </div>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleSave}>
            {buttonText}
          </Button>{" "}
          <Button color="secondary" onClick={toggle}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={confirmModal} toggle={() => setConfirmModal(!confirmModal)} centered>
        <ModalHeader toggle={() => setConfirmModal(!confirmModal)}>Confirm Deletion</ModalHeader>
        <ModalBody>
          Are you sure you want to delete this variant type? This action cannot be undone.
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={confirmDelete}>Delete</Button>{' '}
          <Button color="secondary" onClick={() => setConfirmModal(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default Variants;
